
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import 'styles/globals.css';
import { Box, ChakraProvider } from '@chakra-ui/react';
import LayoutDefault, { LayoutType } from 'components/layout/Default';
import { HeadingsCheck } from 'components/pages/HeadingsCheck';
import { geoCountries } from 'constants/geo-countries';
import { GTM_ID, pageView } from 'lib/gtm';
import { languageMapper } from 'lib/languages';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactElement, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { withTranslations } from 'store/translations';
import Fonts from 'styles/fonts';
import lightTheme from 'themes/lightTheme';
import { getHost } from 'utils/getHost';
import { setZustandTranslations } from 'utils/setZustandTranslations';
import { GetContentPage } from 'utils/sitemap';
export function App({ Component, pageProps }: AppProps): ReactElement {
    const router = useRouter();
    const [cookie, setCookie] = useCookies();
    const translations = withTranslations();
    const [layout, setLayout] = useState<LayoutType>();
    const layoutCodenames = pageProps.layout ?? pageProps.pageProps?.layout;
    // const hasFrontpageCarousel = pageProps?.homepage?.elements.content.value[0].includes('front_page_carousel');
    // pageProps.layout is for content type pages, pageProps.pageProps is for overview type pages
    const variants = (pageProps.variants ?? pageProps.pageProps?.variants) as Awaited<ReturnType<typeof GetContentPage.allLocales>> | undefined;
    const updateLocale = (locale = process.env.NEXT_PUBLIC_NEXT_LOCALE!) => {
        // if (window.location.href.includes("ramboll-wins-million-dollar-tender-for-the-north-sea-energy-island")) {
        //   locale = "da-dk";
        //   window.location.href = "https://www.ramboll.com/da-dk/nyheder/ramboll-wins-million-dollar-tender-for-the-north-sea-energy-island";
        //   return;
        // }
        setCookie('NEXT_LOCALE', locale, { path: '/' });
        setZustandTranslations(translations, locale);
    };
    const updateLayout = async () => {
        let url = `/api/layout?locale=${router.locale}`;
        const remove_dup_nav = layoutCodenames != undefined &&
            Object.keys(layoutCodenames).length !== 0 &&
            Object.keys(layoutCodenames).includes('navigationCodename') &&
            layoutCodenames?.navigationCodename.split('_');
        const remove_dup_footer = layoutCodenames != undefined &&
            Object.keys(layoutCodenames).length !== 0 &&
            Object.keys(layoutCodenames).includes('footerCodename') &&
            layoutCodenames?.footerCodename.split('_');
        const navigation_codename = layoutCodenames != undefined &&
            Object.keys(layoutCodenames).length !== 0 &&
            remove_dup_nav != undefined &&
            Object.keys(layoutCodenames).includes('navigationCodename') &&
            remove_dup_nav
                .filter(function (value, index, self) {
                return self.indexOf(value) === index;
            })
                .join('_');
        const footer_codename = layoutCodenames != undefined &&
            Object.keys(layoutCodenames).length !== 0 &&
            remove_dup_footer != undefined &&
            Object.keys(layoutCodenames).includes('footerCodename') &&
            remove_dup_footer
                .filter(function (value, index, self) {
                return self.indexOf(value) === index;
            })
                .join('_');
        if (layoutCodenames?.navigationCodename)
            url += `&navigationCodename=${navigation_codename}`;
        if (layoutCodenames?.footerCodename)
            url += `&footerCodename=${footer_codename}`;
        if (layoutCodenames?.partOfCampaignCodename)
            url += `&partOfCampaignCodename=${layoutCodenames.partOfCampaignCodename}`;
        const layout = (await fetch(url).then((res) => res.json())) as LayoutType;
        layout.navigation.isCampaign = !!layoutCodenames?.navigationCodename;
        setLayout(layout);
    };
    useEffect(() => {
        router.events.on('routeChangeComplete', pageView);
        return () => {
            router.events.off('routeChangeComplete', pageView);
        };
    }, [router.events]);
    useEffect(() => {
        router.beforePopState(({ options }) => {
            updateLocale(options.locale as string);
            return true;
        });
    });
    useEffect(() => {
        updateLocale(router.locale);
    }, [router.locale, translations.isReady]);
    useEffect(() => {
        router.isReady && updateLayout();
    }, [router.locale, layoutCodenames, router.isReady]);
    useEffect(() => {
        // const isBrowser = navigator.userAgent.match(/chrome|chromium|crios|firefox|fxios|safari|opr|edg/i);
        // if ( !router.isReady) return;
        // google consent script
        const googleConsentScript = document.getElementById('GoogleConsentScript');
        if (!googleConsentScript) {
            const googleConsentScriptTag = document.createElement('script');
            googleConsentScriptTag.setAttribute('id', 'GoogleConsentScript');
            googleConsentScriptTag.setAttribute('data-cookieconsent', 'ignore');
            googleConsentScriptTag.innerHTML = `              
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments)
      }
      gtag("consent", "default", {
        wait_for_update: 500
      });
      gtag("set", "ads_data_redaction", true);
      gtag("set", "url_passthrough", true);
      `;
            googleConsentScriptTag.setAttribute('type', 'text/javascript');
            document.head.appendChild(googleConsentScriptTag);
        }
        // google tag manager script
        // const gtmScript = document.getElementById('gtag-base');
        // if (!gtmScript) {
        //   const gtmScriptTag = document.createElement('script');
        //   gtmScriptTag.setAttribute('id', 'gtag-base');
        //   gtmScriptTag.setAttribute('data-cookieconsent', 'ignore');
        //   gtmScriptTag.setAttribute('strategy', 'afterInteractive');
        //   gtmScriptTag.setAttribute('type', 'text/javascript');
        //   gtmScriptTag.innerHTML = `
        //   (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        //   new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        //   j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        //   'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        //   })(window,document,'script','dataLayer','${GTM_ID}');
        //   `;
        //   document.head.appendChild(gtmScriptTag);
        // }
        if (!cookie.NEXT_LOCALE)
            setCookie('NEXT_LOCALE', router.locale);
    }, []);
    const shouldShowHierarchy = router.query.showHierarchy === 'true' && process.env.NEXT_PUBLIC_APP_ENV === 'development';
    const [documentContent, setDocumentContent] = useState<string>();
    useEffect(() => {
        setDocumentContent(document.getElementsByTagName('main')[0]?.innerHTML);
    }, [router.isReady]);
    return (<>
      <Head>
        <link rel='preload' href='/fonts/Nunito-VariableFont_wght.woff2' as='font' type='font/woff2' crossOrigin='true'/>
        <link rel='preload' href='/public/fonts/Raleway-VariableFont_wght.ttf' as='font' type='font/ttf' crossOrigin='true'/>
        {variants &&
            geoCountries.map((country) => {
                const slug = variants[languageMapper[country.hreflang]];
                if (slug)
                    return (<link key={country.regionCode} rel='alternate' hrefLang={country.hreflang} href={`${getHost()}${country.locale !== process.env.NEXT_PUBLIC_DEFAULT_LOCALE! ? `/${country.locale}` : ''}${slug}`}/>);
            })}
        <script id='gtm' dangerouslySetInnerHTML={{
            __html: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://data.ramboll.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');
      `,
        }}/>
      </Head>
      <ChakraProvider theme={lightTheme}>
        <Script nonce={cookie['nonce']} id='KontentAiCustomElement' src='https://app.kontent.ai/js-api/custom-element/v1/custom-element.min.js'/>

        <Fonts />

        <Box display={shouldShowHierarchy ? 'none' : ''}>
          <LayoutDefault navMenu={layout?.navigation} footer={layout?.footer} 
    // hasFrontpageCarousel={hasFrontpageCarousel}
    variants={variants}>
            <Component {...pageProps}/>
          </LayoutDefault>
        </Box>
        {documentContent && shouldShowHierarchy && <HeadingsCheck docuementContent={documentContent}/>}
      </ChakraProvider>
    </>);
}
const __Next_Translate__Page__1a0e78545a7__ = App;

    export default __appWithI18n(__Next_Translate__Page__1a0e78545a7__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
    });
  